import { useConfig, useValidation, type Rules } from '@seek/libs-shared';
import { Heading, Stack } from 'braid-design-system';
import { useEffect } from 'react';
import { DEFAULT_VARIANT_SCHEMA_VERSION } from '../../../shared/constants/launchDarklyFeatureFlags';
import {
  CURRENT_PAGE_CAREER_DEVELOPMENT,
  trackContinuePressed,
  trackDisplayPage,
  trackSkipQuestionPressed,
} from '../../../shared/tracking/writeAReview/trackingEvents';
import { ReviewPageLayoutNoStepper } from '../../components/ReviewPageLayout/ReviewPageLayoutNoStepper';
import type { Rating } from '../../components/StarSelector/StarSelector';
import { CareerOpportunityRatingQuestion } from '../../questions/RatingQuesions/CareerOpportunityRatingQuestion';
import { CareerDevelopmentDetailsQuestion } from '../../questions/SimpleQuestions/CareerDevelopmentDetailsQuestion';
import {
  ExperimentTwoProgress,
  type PageProps,
  type ReviewPayload,
} from '../../type';
import { validationRules } from '../../validation/validationRules';

type Props = PageProps & {
  isSubmitting: boolean;
};

export const CareerDevelopmentPage = ({
  review,
  reviewFormContext,
  setPage,
  setter,
  pageNumber,
  isSubmitting,
  onSubmit,
}: Props) => {
  const config = useConfig();

  useEffect(() => {
    if (reviewFormContext?.variant) {
      trackDisplayPage({
        config,
        context: reviewFormContext,
        currentPage: CURRENT_PAGE_CAREER_DEVELOPMENT,
        variant: reviewFormContext.variant,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewFormContext?.variant]);

  const fields = {
    careerOpportunityDescription: review?.careerOpportunityDescription,
  };

  const { careerOpportunityDescription } = validationRules(config.language);

  const rules = { careerOpportunityDescription } as Rules<typeof fields>;

  const {
    getTone,
    getMessage,
    handleValueChangeWithValidation,
    validateWithErrors,
  } = useValidation<ReviewPayload>(fields, rules);

  const handleOnContinue = () => {
    const { isValid } = validateWithErrors();

    if (onSubmit) {
      onSubmit();
    }

    trackContinuePressed({
      config,
      context: reviewFormContext,
      validation: isValid ? 'success' : 'mandatory_questions_not_answered',
      currentPage: CURRENT_PAGE_CAREER_DEVELOPMENT,
      variant: reviewFormContext.variant ?? DEFAULT_VARIANT_SCHEMA_VERSION,
    });

    if (!isValid) {
      return;
    }
  };

  const handleOnSkip = () => {
    trackSkipQuestionPressed({
      config,
      context: reviewFormContext,
      currentPage: CURRENT_PAGE_CAREER_DEVELOPMENT,
      variant: reviewFormContext?.variant ?? DEFAULT_VARIANT_SCHEMA_VERSION,
    });

    setter({
      ...review,
      careerOpportunityRating: undefined,
      careerOpportunityDescription: undefined,
    });

    setPage(ExperimentTwoProgress.Management);
  };

  const handleOnChange =
    (updateProperty: keyof ReviewPayload) => (value: Rating) => {
      setter({
        ...review,
        [updateProperty]: value,
      });
    };

  return (
    <ReviewPageLayoutNoStepper
      progress={pageNumber}
      onContinue={handleOnContinue}
      onSkip={handleOnSkip}
      isContinueLoading={isSubmitting}
    >
      <Stack space="large">
        <Heading level="1">Career development</Heading>
        <Stack space="small">
          <CareerOpportunityRatingQuestion
            onChange={handleOnChange('careerOpportunityRating')}
            value={review?.careerOpportunityRating}
            title="Rate development opportunities"
            isRequired={true}
          />
          <CareerDevelopmentDetailsQuestion
            onChange={handleValueChangeWithValidation({
              setter,
              validatorProperty: 'careerOpportunityDescription',
              previousValue: review,
            })}
            value={review?.careerOpportunityDescription}
            tone={getTone('careerOpportunityDescription')}
            message={getMessage('careerOpportunityDescription')}
          />
        </Stack>
      </Stack>
    </ReviewPageLayoutNoStepper>
  );
};
